import React, { useEffect } from 'react';

interface UseInputFocusParams {
    inputFieldRef: React.RefObject<HTMLTextAreaElement>;
    tabSelected: 'internal' | 'reply_external';
    allowInputInExternalReply: boolean;
    isChatSwitched: boolean;
    setIsChatSwitched: (value: boolean) => void;
    ticketKey: string;
    autoFocus?: boolean;
}

/**
 * Hook to manage focus behavior for the ticket input field
 * Handles focus when switching tabs, switching tickets, and permission changes
 *
 * Pass `autoFocus: false` to keep the input from grabbing focus (e.g. in the
 * ticket overview popover, where the composer shouldn't steal focus on open).
 * The caret-positioning and blur-when-disallowed behaviours still apply.
 */
export const useInputFocus = ({
    inputFieldRef,
    tabSelected,
    allowInputInExternalReply,
    isChatSwitched,
    setIsChatSwitched,
    ticketKey,
    autoFocus = true,
}: UseInputFocusParams) => {
    // Handle focus when switching tickets
    useEffect(() => {
        if (inputFieldRef.current && isChatSwitched) {
            const inputElement = inputFieldRef.current;
            const textLength = inputElement.value.length;

            if (
                autoFocus &&
                (tabSelected === 'internal' || allowInputInExternalReply)
            ) {
                inputElement.focus();
            }
            inputElement.setSelectionRange(textLength, textLength);

            setIsChatSwitched(false);
        }
    }, [
        allowInputInExternalReply,
        autoFocus,
        inputFieldRef,
        isChatSwitched,
        tabSelected,
        setIsChatSwitched,
    ]);

    // Mark that we switched tickets
    useEffect(() => {
        setIsChatSwitched(true);
    }, [ticketKey, setIsChatSwitched]);

    // Blur when external reply is not allowed
    useEffect(() => {
        if (tabSelected === 'reply_external' && !allowInputInExternalReply) {
            inputFieldRef?.current?.blur();
        }
    }, [allowInputInExternalReply, tabSelected, inputFieldRef]);

    // Focus when switching tabs or gaining permission
    useEffect(() => {
        if (
            autoFocus &&
            (tabSelected === 'internal' ||
                (tabSelected === 'reply_external' && allowInputInExternalReply))
        ) {
            inputFieldRef?.current?.focus();
        }
    }, [autoFocus, allowInputInExternalReply, tabSelected, inputFieldRef]);
};
